import { useMemo } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Condition } from 'Component/others';
import FilterSelect from 'UiKit/filter-select';
import { TextSemiBold } from 'UiKit/typography';
import useGetAllAccountLivestream from 'Domain/schedule/useGetAllAccountLivestream';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetArea from 'Domain/studio/useGetArea';
import useGetEcommerce from 'Domain/ecommerce/useGetEcommerce';
import useGetScheduleHostList from 'Domain/schedule/useGetScheduleHostList';
import useGetAllOperator from 'Domain/schedule/useGetAllOperator';
import useGetAllStudio from 'Domain/schedule/useGetAllStudio';
import { CloseIcon } from 'Asset/icon';

import checkIsResetAllFilter from 'View/schedule/helper/checkIsResetAllFilter';
import { useScheduleState } from 'View/schedule/state';

import styles from './styles.module.css';

const Filter = () => {
  const { t } = useTranslation('schedule');
  const { filterState, updateFilterState, resetFilterState } = useScheduleState();

  const isResetAllFilter = checkIsResetAllFilter(filterState);

  const optionsBrand = useGetBrand();
  const optionsAccountLivestream = useGetAllAccountLivestream({ brand: filterState?.brand, platform: filterState?.platform });
  const optionsHost = useGetScheduleHostList({
    payload: {
      brandName: filterState?.brand?.value,
      startedAt: filterState?.dateRange?.startDate,
      endedAt: filterState?.dateRange?.endDate,
    },
  });
  const optionsEcommerce = useGetEcommerce({ enabled: true });
  const optionsOperator = useGetAllOperator();
  const optionsStudio = useGetAllStudio();
  const optionsArea = useGetArea();

  const handleFilterChange = (selectedOption, filterKey) => {
    // no filter name
    if (!filterKey) return;

    const newFilters = { [filterKey]: selectedOption };

    updateFilterState(newFilters);
  };

  const resetFilter = () => resetFilterState();

  const optionsAccount = useMemo(() => {
    return optionsAccountLivestream?.data?.map((item) => ({
      ...item,
      label: `${item.label} (${item.ecommerceName})`,
    }));
  }, [optionsAccountLivestream]);

  return (
    <Box className={styles['filter__container']}>
      <Flex className={styles['filter__container-flex']}>
        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.brand}
            onChange={(option) => handleFilterChange(option, 'brand')}
            placeholder={`${t('fieldAccountSelect')} Brand`}
            clearable={true}
            options={optionsBrand?.data}
            isLoading={optionsBrand?.isLoading}
            width="152px"
          />
        </Box>

        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.platform}
            onChange={(option) => handleFilterChange(option, 'platform')}
            placeholder={`${t('fieldAccountSelect')} Platform`}
            clearable={true}
            width="152px"
            isLoading={optionsEcommerce?.isLoading}
            options={optionsEcommerce?.data}
          />
        </Box>

        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.account}
            onChange={(option) => handleFilterChange(option, 'account')}
            placeholder={`${t('fieldAccountSelect')} Account`}
            clearable={true}
            isMulti={true}
            counterLabel={t('accountsLabel')}
            width="152px"
            isLoading={optionsAccountLivestream?.isLoading}
            options={optionsAccount || []}
          />
        </Box>

        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.host}
            onChange={(option) => handleFilterChange(option, 'host')}
            placeholder={`${t('fieldAccountSelect')} ${t('fieldHost')}`}
            clearable={true}
            width="152px"
            isLoading={optionsHost?.isLoading}
            options={optionsHost?.data?.map((host) => {
              return { ...host, isDisabled: false };
            })}
          />
        </Box>

        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.operator}
            onChange={(option) => handleFilterChange(option, 'operator')}
            placeholder={`${t('fieldAccountSelect')} ${t('fieldOperator')}`}
            clearable={true}
            width="152px"
            isLoading={optionsOperator?.isLoading}
            options={optionsOperator?.data}
          />
        </Box>

        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.area}
            onChange={(option) => handleFilterChange(option, 'area')}
            placeholder={`${t('fieldAccountSelect')} Area`}
            clearable={true}
            width="152px"
            isLoading={optionsArea?.isLoading}
            options={optionsArea?.data}
          />
        </Box>

        <Box className={styles['select__container']}>
          <FilterSelect
            value={filterState?.studio}
            onChange={(option) => handleFilterChange(option, 'studio')}
            placeholder={`${t('fieldAccountSelect')} Studio`}
            clearable={true}
            width="152px"
            isLoading={optionsStudio?.isLoading}
            options={optionsStudio?.data}
          />
        </Box>

        <Condition statement={isResetAllFilter}>
          <Flex onClick={resetFilter} className={styles['reset-filter__container']} data-testid="schedule__reset-filter">
            <CloseIcon size="14px" color="var(--color-semantic-text)" />
            <TextSemiBold>{t('clearFilters')}</TextSemiBold>
          </Flex>
        </Condition>
      </Flex>
    </Box>
  );
};

export default Filter;
